import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api';
import Modal from '../components/Modal';
import { useAuth } from '../context/AuthContext';
import { AntragSchwimmer } from '../components/Aufnahmeantrag';
import { hinweis } from '../components/Hinweise';

const KURS_INFO = {
  pirat:        { label: 'Pirat',        farbe: '#880e4f', bg: '#fce4ec', emoji: '🏴‍☠️' },
  seepferdchen: { label: 'Seepferdchen', farbe: '#0d47a1', bg: '#e3f2fd', emoji: '🐴' },
  trixi:        { label: 'Trixi',        farbe: '#1b5e20', bg: '#e8f5e9', emoji: '🌟' },
  bronze:       { label: 'Bronze',       farbe: '#e65100', bg: '#fff3e0', emoji: '🥉' },
  silber:       { label: 'Silber',       farbe: '#424242', bg: '#f5f5f5', emoji: '🥈' },
  gold:         { label: 'Gold',         farbe: '#f57f17', bg: '#fffde7', emoji: '🥇' },
};

function calcAlter(geb) {
  if (!geb) return '–';
  return Math.floor((Date.now() - new Date(geb).getTime()) / (365.25 * 24 * 3600 * 1000)) + ' J.';
}
function fmtDate(d) { return d ? new Date(d).toLocaleDateString('de-DE') : '–'; }
function fmtDateInput(d) { return d ? String(d).slice(0, 10) : ''; }

function calcDauer(von, bis) {
  if (!von) return '';
  const start = new Date(von);
  const end = bis ? new Date(bis) : new Date();
  const tage = Math.floor((end - start) / 86400000);
  if (tage < 7) return `${tage} Tage`;
  if (tage < 30) return `${Math.floor(tage / 7)} Wo.`;
  const m = Math.floor(tage / 30);
  return m >= 12 ? `${Math.floor(m / 12)} J. ${m % 12 ? m % 12 + ' Mon.' : ''}` : `${m} Mon.`;
}

const AUSTRITTSGRUND_LABEL = {
  naechste_gruppe: 'Weiter zur nächsten Gruppe',
  vereinsaustritt: 'Vereinsaustritt',
  sonstiges: 'Sonstiges',
};

const ABWESENHEITSGRUND_LABEL = {
  krank: 'Krank', urlaub: 'Urlaub', termin: 'Termin', sonstiges: 'Sonstiges',
};

// Schwelle für Warnbanner bei Einheiten in Folge ohne Entschuldigung
const SCHWELLE_UNENTSCHULDIGT = 3;

export default function SchwimmerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sw, setSw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [einvSaving, setEinvSaving] = useState({});
  const [exportLoading, setExportLoading] = useState(false);

  const kannExportieren = user?.rolle === 'admin' || user?.rolle === 'schwimmadmin';

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/api/admin/schwimmer/${id}`);
      setSw(r.data);
    } catch { /* ignoriert */ }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const openEdit = () => {
    setEditForm({
      vorname: sw.vorname || '', nachname: sw.nachname || '',
      geburtsdatum: fmtDateInput(sw.geburtsdatum), geschlecht: sw.geschlecht || '',
      kontakt_name: sw.kontakt_name || '', kontakt_email: sw.kontakt_email || '',
      kontakt_telefon: sw.kontakt_telefon || '',
      gesundheitliche_hinweise: sw.gesundheitliche_hinweise || '',
      bemerkung: sw.bemerkung || '',
    });
    setEditModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/api/admin/schwimmer/${id}`, editForm);
      setEditModal(false);
      await load();
    } catch (err) {
      hinweis.fehler(err.response?.data?.error || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  const handleEinverstaendnis = async (typ, aktuellErteilt) => {
    setEinvSaving(s => ({ ...s, [typ]: true }));
    try {
      await api.put(`/api/admin/schwimmer/${id}/einverstaendnis`, { typ, erteilt: !aktuellErteilt });
      await load();
    } catch (err) {
      hinweis.fehler(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setEinvSaving(s => ({ ...s, [typ]: false }));
    }
  };

  const handleDsgvoExport = async () => {
    setExportLoading(true);
    try {
      const r = await api.get(`/api/admin/schwimmer/${id}/dsgvo-export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/json' }));
      const link = document.createElement('a');
      link.href = url;
      const datum = new Date().toISOString().slice(0, 10);
      link.download = `dsgvo_export_${sw.nachname}_${sw.vorname}_${id}_${datum}.json`.replace(/[^a-zA-Z0-9_.-]/g, '_');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      hinweis.fehler('Fehler beim Erstellen des DSGVO-Exports');
    } finally {
      setExportLoading(false);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '2rem' }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>;
  if (!sw) return <div className="alert alert-error">Schwimmer nicht gefunden</div>;

  const aktiveZuordnung = sw.verlauf.find(v => !v.ausgetreten_am);
  const aktiveInfo = aktiveZuordnung ? KURS_INFO[aktiveZuordnung.kurs_typ] : null;

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>←</button>
          <h2 className="page-title" style={{ margin: 0 }}>{sw.vorname} {sw.nachname}</h2>
          {aktiveInfo && (
            <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: '0.8rem', fontWeight: 700,
              background: aktiveInfo.bg, color: aktiveInfo.farbe }}>
              {aktiveInfo.emoji} {aktiveZuordnung.gruppe_name}
            </span>
          )}
          {!sw.aktiv_im_verein && (
            <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: '0.8rem', fontWeight: 700,
              background: '#f5f5f5', color: '#616161' }}>Ausgetreten</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {kannExportieren && (
            <button className="btn btn-secondary btn-sm" onClick={handleDsgvoExport} disabled={exportLoading}>
              {exportLoading ? 'Exportiere…' : '📄 DSGVO-Auskunft exportieren'}
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={openEdit}>✏ Stammdaten bearbeiten</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>

        {/* Stammdaten */}
        <div className="card" style={{ padding: '1rem' }}>
          <div style={{ fontWeight: 700, marginBottom: '0.875rem', color: 'var(--primary)' }}>Stammdaten</div>
          <div className="detail-grid">
            <div className="detail-item"><div className="detail-label">Name</div><div className="detail-value">{sw.vorname} {sw.nachname}</div></div>
            <div className="detail-item"><div className="detail-label">Geburtsdatum</div><div className="detail-value">{fmtDate(sw.geburtsdatum)} ({calcAlter(sw.geburtsdatum)})</div></div>
            <div className="detail-item"><div className="detail-label">Geschlecht</div><div className="detail-value" style={{ textTransform: 'capitalize' }}>{sw.geschlecht || '–'}</div></div>
            <div className="detail-item"><div className="detail-label">Im Verein seit</div><div className="detail-value">{fmtDate(sw.created_at)}</div></div>
            {!sw.aktiv_im_verein && (
              <div className="detail-item"><div className="detail-label">Vereinsaustritt</div><div className="detail-value">{fmtDate(sw.vereinsaustritt_datum)}</div></div>
            )}
          </div>
          {sw.gesundheitliche_hinweise && (
            <div style={{ marginTop: '0.75rem', padding: '0.5rem 0.75rem', background: '#fff3e0', borderRadius: 6, fontSize: '0.82rem', color: '#bf360c' }}>
              ⚠ <strong>Gesundheit:</strong> {sw.gesundheitliche_hinweise}
            </div>
          )}
          {sw.bemerkung && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              <strong>Bemerkung:</strong> {sw.bemerkung}
            </div>
          )}
        </div>

        {/* Kontakt */}
        <div className="card" style={{ padding: '1rem' }}>
          <div style={{ fontWeight: 700, marginBottom: '0.875rem', color: 'var(--primary)' }}>Kontaktperson</div>
          <div className="detail-grid">
            <div className="detail-item"><div className="detail-label">Name</div><div className="detail-value">{sw.kontakt_name || '–'}</div></div>
            <div className="detail-item"><div className="detail-label">E-Mail</div>
              <div className="detail-value">
                {sw.kontakt_email ? <a href={`mailto:${sw.kontakt_email}`}>{sw.kontakt_email}</a> : '–'}
              </div>
            </div>
            <div className="detail-item"><div className="detail-label">Telefon</div>
              <div className="detail-value">
                {sw.kontakt_telefon ? <a href={`tel:${sw.kontakt_telefon}`}>{sw.kontakt_telefon}</a> : '–'}
              </div>
            </div>
          </div>
          {aktiveZuordnung && (
            <div style={{ marginTop: '1rem' }}>
              <button className="btn btn-secondary btn-sm"
                onClick={() => navigate(`/gruppen/${aktiveZuordnung.gruppe_id}`)}>
                → Zur Gruppe: {aktiveZuordnung.gruppe_name}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Aufnahmeantrag (Vereinsanmeldung) */}
      <AntragSchwimmer sw={sw} darfBearbeiten={kannExportieren} onGeaendert={load} />

      {/* Einverständniserklärungen */}
      {sw.einverstaendnisse && sw.einverstaendnisse.length > 0 && (
        <div className="card" style={{ marginBottom: '1.25rem' }}>
          <div style={{ padding: '0.875rem 1rem', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: '0.95rem' }}>
            Einverständniserklärungen
          </div>
          <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
            {sw.einverstaendnisse.map(e => (
              <div key={e.typ} style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                padding: '0.625rem 0.75rem', borderRadius: 8,
                background: e.erteilt ? '#e8f5e9' : '#fafafa',
                border: `1px solid ${e.erteilt ? '#c8e6c9' : '#e0e0e0'}`,
              }}>
                <button
                  onClick={() => handleEinverstaendnis(e.typ, e.erteilt)}
                  disabled={!!einvSaving[e.typ]}
                  style={{
                    width: 24, height: 24, borderRadius: 6, flexShrink: 0,
                    border: `2px solid ${e.erteilt ? '#2e7d32' : '#bbb'}`,
                    background: e.erteilt ? '#2e7d32' : '#fff',
                    color: '#fff', fontWeight: 700, fontSize: '0.8rem',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                  {einvSaving[e.typ] ? '…' : e.erteilt ? '✓' : ''}
                </button>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem', color: e.erteilt ? '#2e7d32' : 'var(--text)' }}>
                    {e.label}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    {e.erteilt
                      ? `Erteilt am ${fmtDate(e.erteilt_am)}${e.erfasst_von_name ? ` · erfasst von ${e.erfasst_von_name}` : ''}`
                      : 'Noch nicht erteilt'}
                  </div>
                </div>
              </div>
            ))}
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
              Dokumentiert den erfassten Status – kein Ersatz für eine rechtsverbindliche Unterschrift.
            </div>
          </div>
        </div>
      )}

      {/* Verlaufshistorie */}
      <div className="card">
        <div style={{ padding: '0.875rem 1rem', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: '0.95rem' }}>
          Verlaufshistorie
        </div>

        {sw.verlauf.length === 0 ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)' }}>Noch keine Einträge</div>
        ) : (
          <div style={{ padding: '1.25rem', position: 'relative' }}>
            {/* Zeitstrahl-Linie */}
            <div style={{ position: 'absolute', left: 32, top: 0, bottom: 0, width: 2, background: '#e0e0e0' }} />

            {sw.verlauf.map((v, idx) => {
              const info = KURS_INFO[v.kurs_typ] || { label: v.kurs_typ, farbe: '#333', bg: '#f0f4f8', emoji: '🏊' };
              const isAktiv = !v.ausgetreten_am;
              const dauer = calcDauer(v.eingetreten_am, v.ausgetreten_am);

              return (
                <div key={v.id} style={{ display: 'flex', gap: '1.25rem', marginBottom: idx < sw.verlauf.length - 1 ? '1.5rem' : 0, position: 'relative' }}>
                  {/* Kreis */}
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                    background: isAktiv ? info.farbe : '#e0e0e0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1rem', boxShadow: isAktiv ? `0 0 0 3px ${info.bg}` : 'none',
                    position: 'relative', zIndex: 1,
                  }}>
                    {v.abzeichen_erhalten ? '🎓' : info.emoji}
                  </div>

                  {/* Inhalt */}
                  <div style={{ flex: 1, paddingTop: 4 }}>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                      <span style={{ fontWeight: 700 }}>{v.gruppe_name}</span>
                      <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 700,
                        background: info.bg, color: info.farbe }}>{info.emoji} {info.label}</span>
                      {isAktiv && <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 700,
                        background: '#e8f5e9', color: '#2e7d32' }}>Aktuell</span>}
                    </div>

                    <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                      <span>Eingetreten: <strong>{fmtDate(v.eingetreten_am)}</strong></span>
                      {v.ausgetreten_am && <span>Ausgetreten: <strong>{fmtDate(v.ausgetreten_am)}</strong></span>}
                      {dauer && <span>Dauer: <strong>{dauer}</strong></span>}
                    </div>

                    {v.abzeichen_erhalten && (
                      <div style={{ marginTop: '0.375rem', fontSize: '0.82rem' }}>
                        🎓 <strong>Abzeichen erhalten</strong> am {fmtDate(v.abzeichen_datum)}
                        {v.austrittsgrund && (
                          <span style={{ marginLeft: '0.75rem', color: 'var(--text-muted)' }}>
                            → {AUSTRITTSGRUND_LABEL[v.austrittsgrund] || v.austrittsgrund}
                          </span>
                        )}
                      </div>
                    )}

                    {v.austrittsgrund === 'vereinsaustritt' && !v.abzeichen_erhalten && (
                      <div style={{ marginTop: '0.375rem', fontSize: '0.82rem', color: '#616161' }}>
                        🚪 Vereinsaustritt
                      </div>
                    )}

                    {v.bemerkung && (
                      <div style={{ marginTop: '0.25rem', fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                        {v.bemerkung}
                      </div>
                    )}

                    {v.erstellt_von_vorname && (
                      <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        Erfasst von {v.erstellt_von_vorname} {v.erstellt_von_nachname}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Anwesenheit ───────────────────────────────────────────────── */}
      {sw.fehlzeiten_kennzahlen && sw.fehlzeiten_kennzahlen.letzte_n > 0 && sw.anwesenheit_verlauf.length > 0 && (
        <div className="card" style={{ marginTop: '1.25rem' }}>
          <div style={{ padding: '0.875rem 1rem', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: '0.95rem' }}>
            Anwesenheit <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.8rem' }}>
              (letzte {sw.fehlzeiten_kennzahlen.letzte_n} erfasste Einheiten)
            </span>
          </div>

          <div style={{ padding: '1rem' }}>
            {sw.fehlzeiten_kennzahlen.unentschuldigt_in_folge >= SCHWELLE_UNENTSCHULDIGT && (
              <div style={{
                background: '#ffebee', border: '1px solid #ffcdd2', borderRadius: 8,
                padding: '0.625rem 0.875rem', marginBottom: '1rem',
                fontSize: '0.85rem', color: '#b71c1c', fontWeight: 600,
              }}>
                ⚠ {sw.fehlzeiten_kennzahlen.unentschuldigt_in_folge}× in Folge unentschuldigt gefehlt — Rücksprache empfohlen.
              </div>
            )}

            {/* Ampel-Kennzahlen */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '0.75rem', marginBottom: '1.125rem' }}>
              <div style={{ background: '#e8f5e9', borderRadius: 8, padding: '0.75rem', textAlign: 'center' }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#2e7d32' }}>{sw.fehlzeiten_kennzahlen.anwesend}</div>
                <div style={{ fontSize: '0.72rem', color: '#2e7d32' }}>Anwesend</div>
              </div>
              <div style={{ background: '#fff8e1', borderRadius: 8, padding: '0.75rem', textAlign: 'center' }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#f57f17' }}>{sw.fehlzeiten_kennzahlen.entschuldigt}</div>
                <div style={{ fontSize: '0.72rem', color: '#f57f17' }}>Entschuldigt</div>
              </div>
              <div style={{ background: '#ffebee', borderRadius: 8, padding: '0.75rem', textAlign: 'center' }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#c62828' }}>{sw.fehlzeiten_kennzahlen.unentschuldigt}</div>
                <div style={{ fontSize: '0.72rem', color: '#c62828' }}>Unentschuldigt</div>
              </div>
              <div style={{ background: '#f0f4f8', borderRadius: 8, padding: '0.75rem', textAlign: 'center' }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--primary)' }}>
                  {sw.fehlzeiten_kennzahlen.quote_prozent ?? '–'}{sw.fehlzeiten_kennzahlen.quote_prozent !== null && '%'}
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Quote</div>
              </div>
            </div>

            {/* Liste der letzten Einheiten */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {sw.anwesenheit_verlauf.map(a => {
                const farbe = a.anwesend ? '#2e7d32' : a.entschuldigt ? '#f57f17' : '#c62828';
                return (
                  <div key={a.id} style={{
                    display: 'flex', alignItems: 'center', gap: '0.625rem',
                    fontSize: '0.83rem', padding: '0.3rem 0',
                  }}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: farbe, flexShrink: 0 }} />
                    <span style={{ minWidth: 90, color: 'var(--text-muted)' }}>{fmtDate(a.datum)}</span>
                    <span style={{ color: 'var(--text-muted)', flex: 1 }}>{a.gruppe_name}</span>
                    <span style={{ fontWeight: 600, color: farbe }}>
                      {a.anwesend ? 'Anwesend' : a.entschuldigt ? 'Entschuldigt' : 'Unentschuldigt'}
                      {!a.anwesend && a.abwesenheitsgrund && ` · ${ABWESENHEITSGRUND_LABEL[a.abwesenheitsgrund] || a.abwesenheitsgrund}`}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Trainer-Notizen ───────────────────────────────────────────── */}
      {sw.notiz_historie && sw.notiz_historie.length > 0 && (
        <div className="card" style={{ marginTop: '1.25rem' }}>
          <div style={{ padding: '0.875rem 1rem', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: '0.95rem' }}>
            Trainer-Notizen
          </div>
          <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {sw.notiz_historie.map((n, i) => (
              <div key={i} style={{ fontSize: '0.85rem', borderLeft: '3px solid var(--border)', paddingLeft: '0.75rem' }}>
                <div style={{ color: 'var(--text)', marginBottom: '0.2rem' }}>{n.notiz}</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                  {fmtDate(n.datum)} · {n.gruppe_name}
                  {n.markiert_von_name && ` · ${n.markiert_von_name}`}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Edit-Modal ────────────────────────────────────────────────── */}
      {editModal && (
        <Modal title="Stammdaten bearbeiten" onClose={() => setEditModal(false)} size="lg">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem' }}>
            {[
              ['vorname', 'Vorname', 'text'],
              ['nachname', 'Nachname', 'text'],
              ['geburtsdatum', 'Geburtsdatum', 'date'],
              ['kontakt_name', 'Kontakt Name', 'text'],
              ['kontakt_email', 'Kontakt E-Mail', 'email'],
              ['kontakt_telefon', 'Kontakt Telefon', 'tel'],
            ].map(([field, label, type]) => (
              <div key={field} className="form-group" style={{ margin: 0 }}>
                <label className="form-label">{label}</label>
                <input type={type} className="form-input"
                  value={editForm[field] || ''}
                  onChange={e => setEditForm(f => ({ ...f, [field]: e.target.value }))} />
              </div>
            ))}
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Geschlecht</label>
              <select className="form-select" value={editForm.geschlecht || ''}
                onChange={e => setEditForm(f => ({ ...f, geschlecht: e.target.value }))}>
                <option value="">–</option>
                <option value="maennlich">Männlich</option>
                <option value="weiblich">Weiblich</option>
                <option value="divers">Divers</option>
              </select>
            </div>
          </div>
          <div className="form-group" style={{ marginTop: '0.875rem' }}>
            <label className="form-label">Gesundheitliche Hinweise</label>
            <textarea className="form-control" rows={2} value={editForm.gesundheitliche_hinweise || ''}
              onChange={e => setEditForm(f => ({ ...f, gesundheitliche_hinweise: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Interne Bemerkung</label>
            <textarea className="form-control" rows={2} value={editForm.bemerkung || ''}
              onChange={e => setEditForm(f => ({ ...f, bemerkung: e.target.value }))} />
          </div>
          <div className="modal-actions">
            <button className="btn btn-secondary btn-sm" onClick={() => setEditModal(false)}>Abbrechen</button>
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
              {saving ? 'Speichern…' : 'Speichern'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
